import { useEffect, useRef, useState } from 'react';
import { BarChart3, CalendarRange, Inbox, LayoutDashboard, Receipt, Users, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Reveal, SectionHeading } from './primitives';

/**
 * Tabbed tour of the real admin, with screenshots of a demo tenant
 * (src/assets/tour/<key>.webp). Auto-advances until someone picks a tab.
 */
const tourModules = import.meta.glob<{ default: string }>('@/assets/tour/*.webp', { eager: true });
const shot = (key: string) =>
  Object.entries(tourModules).find(([path]) => path.endsWith(`/${key}.webp`))?.[1].default;

const STEPS = [
  {
    key: 'dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    title: 'Your day at a glance',
    body: 'Today’s jobs, who’s working, new booking requests and how the month is going, the moment you log in.',
  },
  {
    key: 'insights',
    icon: BarChart3,
    label: 'Insights',
    title: 'Know your numbers',
    body: 'Is this month beating last month? See who still owes you and which cleans actually make you money.',
  },
  {
    key: 'schedule',
    icon: CalendarRange,
    label: 'Schedule',
    title: 'Plan the day in one view',
    body: 'Every cleaner side by side, jobs nobody’s on flagged, and no double bookings: customers only ever see times that are genuinely free.',
  },
  {
    key: 'enquiries',
    icon: Inbox,
    label: 'Enquiries',
    title: 'Never lose a lead',
    body: 'Every enquiry from your site in one inbox, so you can see which ones still need a reply.',
  },
  {
    key: 'invoices',
    icon: Receipt,
    label: 'Invoices',
    title: 'Get paid without the chasing',
    body: 'Send an invoice in a couple of clicks, then see straight away who’s paid and who’s late.',
  },
  {
    key: 'customers',
    icon: Users,
    label: 'Customers',
    title: 'Know who’s on the phone',
    body: 'Each customer’s bookings, history and messages together, so you’re never caught out.',
  },
  {
    key: 'team',
    icon: Clock,
    label: 'Team & hours',
    title: 'Your team, your hours',
    body: 'Add your cleaners and set when you all work. Your booking page only offers times that fit.',
  },
].filter((s) => shot(s.key));

const ADVANCE_MS = 6500;

export function ProductTour() {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const [paused, setPaused] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!auto || paused || STEPS.length < 2) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = window.setTimeout(() => setActive((i) => (i + 1) % STEPS.length), ADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [active, auto, paused]);

  if (STEPS.length === 0) return null;
  const step = STEPS[active];

  const choose = (i: number) => {
    setAuto(false);
    setActive(i);
  };
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const delta = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    const next = (i + delta + STEPS.length) % STEPS.length;
    choose(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section className="py-28 md:py-36" aria-labelledby="tour-heading">
      <div className="container mx-auto px-4">
        <SectionHeading
          id="tour-heading"
          eyebrow="Take the tour"
          title={
            <>
              Have a look round <span className="mk-mark mk-mark-mint">before you sign up.</span>
            </>
          }
          lede="These are real screens from the app, filled in with a made-up cleaning company. It’s exactly what you’ll see when you log in."
        />

        <div
          className="mt-14 grid gap-8 lg:grid-cols-[320px_1fr] lg:gap-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div role="tablist" aria-label="Product features" aria-orientation="vertical" className="mk-tour-tabs flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const selected = i === active;
              return (
                <button
                  key={s.key}
                  ref={(el) => (tabRefs.current[i] = el)}
                  type="button"
                  role="tab"
                  id={`tour-tab-${s.key}`}
                  aria-selected={selected}
                  aria-controls="tour-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => choose(i)}
                  onKeyDown={(e) => onKey(e, i)}
                  className={cn('mk-tour-tab', selected && 'is-active')}
                >
                  <span className="mk-tour-icon">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 text-left">
                    <span className="block font-display text-[15px] font-semibold">{s.label}</span>
                    <span className="mk-tour-desc">{s.body}</span>
                  </span>
                  {selected && auto && !paused && (
                    <span key={active} className="mk-tour-progress" style={{ animationDuration: `${ADVANCE_MS}ms` }} aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>

          <Reveal delay={120}>
            <div id="tour-panel" role="tabpanel" aria-labelledby={`tour-tab-${step.key}`} className="mk-tour-frame">
              <div className="mk-tour-chrome" aria-hidden="true">
                <span />
                <span />
                <span />
                <p>app.thepolishedversion.com/admin</p>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden bg-[#f4f5f7]">
                {STEPS.map((s, i) => (
                  <img
                    key={s.key}
                    src={shot(s.key)}
                    alt={`${s.label}: ${s.title}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    className={cn('mk-tour-shot', i === active && 'is-active')}
                    aria-hidden={i !== active}
                  />
                ))}
              </div>
            </div>
            <p className="mt-5 font-display text-lg font-semibold text-[hsl(var(--mk-ink))] lg:hidden">{step.title}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
